import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import { useCartStore } from "@tellescope/shop";
import { createEnduserAuthSlice } from "./slices/authSlice";
import { createVideoCallingSlice } from "./slices/videoCallingSlice";
import type { EnduserStore, PersistedSessionData } from "./types";

// Create the enduser store - completely separate from admin store
export const useEnduserStore = create<EnduserStore>()(
  devtools(
    persist(
      (set, get, api) => {
        const authSlice = createEnduserAuthSlice(set, get, api);
        const videoCallingSlice = createVideoCallingSlice(set, get, api);

        return {
          ...authSlice,
          videoCalling: videoCallingSlice,

          resetAllState: () => {
            get().reset();
            get().videoCalling.reset();
          },
        };
      },
      {
        name: "tellescope-portal-enduser-store",
        partialize: (state) => {
          // Persist session data so we can restore on page refresh
          if (state.session && state.currentEnduser) {
            return {
              persistedSession: {
                authToken: state.session.authToken,
                businessId: state.session.businessId,
                enduser: state.currentEnduser,
              } as PersistedSessionData,
            };
          }
          return { persistedSession: null };
        },
        onRehydrateStorage: () => (state, error) => {
          if (error) {
            console.error("Error rehydrating enduser store:", error);
            return;
          }
          // After rehydration, restore the session if we have persisted data
          const persistedData = (state as unknown as { persistedSession?: PersistedSessionData })?.persistedSession;
          if (persistedData && state) {
            state.restoreSession(persistedData);
          }
        },
      }
    ),
    {
      name: "portal-enduser-store",
    }
  )
);

// Covers every sign-in path in one place, including magic links and the admin "view as enduser"
// picker, which switch endusers without a logout. The first call handles the session restored from
// storage during the store's creation, which happens before the subscription exists.
const claimCartForEnduser = (enduserId: string | undefined) => {
  if (enduserId) useCartStore.getState().claimCart(enduserId);
};
claimCartForEnduser(useEnduserStore.getState().currentEnduser?.id);
useEnduserStore.subscribe((state, previous) => {
  if (state.currentEnduser?.id !== previous.currentEnduser?.id) claimCartForEnduser(state.currentEnduser?.id);
});

// Enduser auth selector for convenience
export const useEnduserAuth = () =>
  useEnduserStore(
    useShallow((state) => ({
      session: state.session,
      isLoading: state.isLoading,
      currentEnduser: state.currentEnduser,
      tokenError: state.tokenError,
      initializeFromToken: state.initializeFromToken,
      logout: state.logout,
      clearAuth: state.clearAuth,
      setTokenError: state.setTokenError,
    }))
  );

// Global reset hook
export const useEnduserResetStore = () => useEnduserStore((state) => state.resetAllState);

// Video calling selector for convenience
export const useVideoCalling = () => useEnduserStore((state) => state.videoCalling);
